import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, Trash2, X, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { getEquipment, submitHireQuote, apiError } from "../lib/api";

const field = "w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export default function Hire() {
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState({}); // id -> {item, qty}
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", organisation: "", date_from: "", date_to: "", event_details: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { getEquipment().then(setItems).catch(() => {}); }, []);

  const categories = useMemo(() => {
    const map = {};
    items.forEach((i) => { (map[i.category] = map[i.category] || []).push(i); });
    return map;
  }, [items]);

  const cartList = Object.values(cart);
  const cartCount = cartList.reduce((n, c) => n + c.qty, 0);

  const add = (item) => setCart((c) => ({ ...c, [item.id]: { item, qty: (c[item.id]?.qty || 0) + 1 } }));
  const dec = (id) => setCart((c) => {
    const cur = c[id]; if (!cur) return c;
    if (cur.qty <= 1) { const { [id]: _, ...rest } = c; return rest; }
    return { ...c, [id]: { ...cur, qty: cur.qty - 1 } };
  });
  const removeItem = (id) => setCart((c) => { const { [id]: _, ...rest } = c; return rest; });

  const submit = async (e) => {
    e.preventDefault();
    if (!cartList.length) { toast.error("Add some equipment to your quote first."); return; }
    setBusy(true);
    try {
      await submitHireQuote({ ...form, items: cartList.map((c) => ({ equipment_id: c.item.id, name: c.item.name, quantity: c.qty })) });
      toast.success("Quote request sent. We'll be in touch soon.");
      setCart({}); setOpen(false);
      setForm({ customer_name: "", email: "", phone: "", organisation: "", date_from: "", date_to: "", event_details: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader label="NEAD Hire" titleLines={["Event equipment", "and expertise."]}
        intro="Staging, sound, lighting, marquees and full event support, hired out across Cumbria and beyond. Build a list, request a quote, and we'll take it from there." />

      <section className="container-x py-20 md:py-28">
        {items.length > 0 ? (
          Object.entries(categories).map(([cat, list]) => (
          <div key={cat} className="mb-16">
            <Reveal><Eyebrow className="text-nead-pink">{cat}</Eyebrow></Reveal>
            <Stagger className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.map((item) => (
                <StaggerItem key={item.id}>
                  <div data-testid={`hire-item-${item.id}`} className="rounded-[1.5rem] overflow-hidden bg-white border border-black/[0.07] shadow-sm h-full flex flex-col">
                    <div className="aspect-[16/11] overflow-hidden bg-nead-mist">{item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}</div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-display font-bold text-lg text-nead-navy">{item.name}</h3>
                      <p className="mt-2 text-sm text-nead-navy/60 flex-1">{item.description}</p>
                      <div className="mt-4 flex items-center justify-between">
                        {item.day_rate > 0 ? <span className="font-display font-bold text-nead-navy">£{Number(item.day_rate).toFixed(2)}<span className="text-xs font-normal text-nead-navy/45">/day</span></span> : <span className="text-sm text-nead-navy/45">POA</span>}
                        {cart[item.id] ? (
                          <div className="flex items-center gap-2">
                            <button onClick={() => dec(item.id)} className="h-8 w-8 rounded-full border border-black/15 flex items-center justify-center hover:border-nead-pink" data-testid={`hire-dec-${item.id}`}><Minus className="h-3.5 w-3.5" /></button>
                            <span className="w-5 text-center text-sm font-semibold" data-testid={`hire-qty-${item.id}`}>{cart[item.id].qty}</span>
                            <button onClick={() => add(item)} className="h-8 w-8 rounded-full border border-black/15 flex items-center justify-center hover:border-nead-pink" data-testid={`hire-inc-${item.id}`}><Plus className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <button onClick={() => add(item)} data-testid={`hire-add-${item.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-nead-navy px-4 py-2 text-sm font-semibold text-white hover:bg-nead-pink transition-colors"><Plus className="h-4 w-4" /> Add</button>
                        )}
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        ))
        ) : (
          <div className="mx-auto max-w-2xl text-center rounded-[2rem] border border-black/[0.07] bg-nead-mist/60 px-8 py-20 md:py-24" data-testid="hire-coming-soon">
            <span className="inline-flex items-center gap-2 rounded-full bg-nead-cyan/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-nead-cyan">
              <Sparkles className="h-4 w-4" /> Coming soon
            </span>
            <h2 className="mt-6 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy">Our hire catalogue is on its way.</h2>
            <p className="mt-4 text-nead-navy/60 leading-relaxed">
              We're putting the finishing touches to our equipment hire range, staging, sound, lighting, marquees and full event support. In the meantime, get in touch and we'll help with your event.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button to="/contact" variant="pink" testId="hire-contact">Talk to us about your event</Button>
            </div>
          </div>
        )}
      </section>

      {/* Floating cart button */}
      {cartCount > 0 && (
        <button onClick={() => setOpen(true)} data-testid="hire-cart-btn" className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2.5 rounded-full grad-pink-cyan px-6 py-4 text-white font-semibold shadow-2xl hover:scale-105 transition-transform">
          <ShoppingCart className="h-5 w-5" /> Request quote
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-nead-navy text-xs font-bold">{cartCount}</span>
        </button>
      )}

      {/* Cart / quote drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setOpen(false)} data-testid="hire-drawer">
          <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/5 sticky top-0 bg-white">
              <h3 className="font-display font-bold text-lg text-nead-navy">Your quote request</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-black/5" data-testid="hire-drawer-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-3">
                {cartList.map((c) => (
                  <div key={c.item.id} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-nead-navy">{c.item.name}</div>
                      <div className="text-xs text-nead-navy/50">Qty {c.qty}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => dec(c.item.id)} className="h-7 w-7 rounded-full border border-black/15 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                      <button onClick={() => add(c.item)} className="h-7 w-7 rounded-full border border-black/15 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                      <button onClick={() => removeItem(c.item.id)} className="h-7 w-7 rounded-full text-red-500 flex items-center justify-center hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
                {!cartList.length && <p className="text-sm text-nead-navy/50">Your list is empty.</p>}
              </div>

              <form onSubmit={submit} className="mt-6 space-y-3 border-t border-black/5 pt-6" data-testid="hire-quote-form">
                <input required placeholder="Your name" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} className={field} data-testid="hire-name" />
                <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={field} data-testid="hire-email" />
                <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={field} data-testid="hire-phone" />
                <input placeholder="Organisation (optional)" value={form.organisation} onChange={(e) => setForm((f) => ({ ...f, organisation: e.target.value }))} className={field} />
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold uppercase tracking-wider text-nead-navy/50">From</span><input type="date" value={form.date_from} onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))} className={`${field} mt-1`} data-testid="hire-date-from" /></label>
                  <label className="block"><span className="text-xs font-semibold uppercase tracking-wider text-nead-navy/50">To</span><input type="date" value={form.date_to} onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))} className={`${field} mt-1`} data-testid="hire-date-to" /></label>
                </div>
                <textarea rows={3} placeholder="Tell us about your event" value={form.event_details} onChange={(e) => setForm((f) => ({ ...f, event_details: e.target.value }))} className={`${field} resize-none`} data-testid="hire-details" />
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-nead-navy transition-colors disabled:opacity-60" data-testid="hire-quote-submit">{busy ? "Sending…" : "Send quote request"}</button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
